import type { JournalEntry, SaleVerdict } from '@krp/client-runtime'
import { formatKopecks, priceInputText, quantityError, type CartLineVm, type PayButtonVm, type PickerVm, type SaleCheckReasonVm, type SaleCheckVm, type SaleCommandVm, type SaleFeedback, type SaleNoticeVm, type SaleOperationVm, type SaleVm, type SearchVm, type StateBadgeVm } from '@krp/sale-ui'
import { cartProblems, cartTotal, lineTotal, type CartLine, type CartProblem } from './cart'

import type { AgentSessionModel } from '../../agent-session/model/agentSessionController'
import type { OwnPaymentAttempt } from './paymentAttemptSlice'
import { buildOwnPaymentFlow } from './paymentFlow'
import { quantityText, type OwnPaymentKind, type OwnSaleState } from './saleState'
import { initialOwnWords, type OwnNotice, type OwnWordsState } from '../../operation/model/words'

export { unresolvedOperationId } from './paymentFlow'

/**
 * Модель экрана продажи источника «Собственная»: корзина окна + данные агента → нейтральный `SaleVm` общего экрана. Чистая функция,
 * ничего не читает и не отправляет. Чего у источника нет (отложенные чеки, «чек уже пробит на кассе», своя цена кассира, возврат
 * из окна), там нейтральные значения: кнопок нет, экран их не показывает.
 *
 * Правила честности: продажа закрыта, пока агент не сказал «можно» (порог `gate`), строка без цены или ставки НДС чек не
 * пропускает, а «неизвестно» не выдаётся ни за «продано», ни за «не продано».
 */

export interface OwnCatalogView {
  search: SearchVm
  picker: PickerVm
  /** «Цены прайса на 14:32» — время снимка; `null` — снимка нет. */
  pricesAtText: string | null
}

export interface OwnSaleVmInput {
  resourceId: string
  state: OwnSaleState
  session: AgentSessionModel
  catalog: OwnCatalogView
  /** Что набрано в формах хода проведения и какие заметки висят; нет - ничего. */
  words?: OwnWordsState
  /** Попытка оплаты окна (RP-PAY-ONE): ключ окна, «Свернуть», «Закрыть», «команда не ушла»; нет — оплату здесь не нажимали. */
  attempt?: OwnPaymentAttempt | null
  /** Кассир подтверждён (или повтор начатой операции идёт по её снимку): без этого «Повторить оплату» недоступна. По умолчанию — да. */
  cashierReady?: boolean
}

const READY_QUERY = { status: 'ready', refreshing: false, error: null } as const

const PAYMENT_TEXT: Readonly<Record<OwnPaymentKind, string>> = { Cash: 'Наличными', Card: 'Картой' }

const KIND_TEXT: Readonly<Record<JournalEntry['kind'], string>> = { receipt: 'Чек', shift: 'Смена', cash: 'Внесение или выплата наличных' }

const reasonOf = (problem: CartProblem): SaleCheckReasonVm => ({
  code: problem.code,
  text: problem.text,
  detail: null,
  // Ставка НДС исправляется во вкладке «Настройка» номенклатуры — это не касса; цену кассир меняет выбором другого вида цены.
  fixBy: problem.code === 'no-vat' || problem.code === 'profile-incomplete' ? 'administrator' : 'cashier',
  removable: true,
})

function lineVm(line: CartLine, state: OwnSaleState, reasons: readonly CartProblem[]): CartLineVm {
  const draft = state.quantityDrafts[line.id]
  const quantity = draft ?? quantityText(line.quantityMilli)
  const total = lineTotal(line)
  return {
    key: String(line.id),
    productKey: line.productKey,
    barcode: line.barcode,
    name: line.name,
    quantity,
    unit: line.unit,
    isWeight: line.isWeight,
    priceText: line.priceKopecks === null ? null : formatKopecks(line.priceKopecks),
    amountText: total === null ? null : formatKopecks(total),
    amountKopecks: total,
    quantityError: quantityError(quantity, line.isWeight),
    // Своей цены у кассира в этом источнике нет: цена — из прайса.
    price: null,
    catalogPrice: line.priceKopecks === null ? null : priceInputText(line.priceKopecks),
    priceError: null,
    catalogPriceText: null,
    priceChangedBy: null,
    selected: state.selectedLineId === line.id,
    blocked: reasons.filter((problem) => problem.lineId === line.id).map(reasonOf),
  }
}

const badge = (label: string, text: string, tone: StateBadgeVm['tone'], icon: StateBadgeVm['icon']): StateBadgeVm => ({ label, text, tone, icon })

function badges(state: OwnSaleState, verdict: SaleVerdict | null): { money: StateBadgeVm | null; receipt: StateBadgeVm | null } {
  switch (state.phase) {
    case 'composing':
      return { money: null, receipt: null }
    case 'paying':
      return {
        money: badge('Оплата', state.paidKind === 'Card' ? 'Идёт на терминале' : 'Принимаем наличные', 'neutral', 'progress'),
        receipt: badge('Чек', 'Ждёт оплаты', 'neutral', 'idle'),
      }
    case 'finished':
      return {
        money: badge('Оплата', 'Оплачено', 'success', 'ok'),
        receipt: badge('Чек', verdict?.kind === 'sold' && verdict.fiscal?.fiscalDocumentNumber != null ? `Пробит, ФД ${verdict.fiscal.fiscalDocumentNumber}` : 'Пробит', 'success', 'ok'),
      }
    case 'failed':
      return {
        money:
          verdict !== null && verdict.kind !== 'sold' && verdict.card?.voided === true
            ? badge('Оплата', 'Платёж отменён, деньги не списаны', 'neutral', 'failed')
            : badge('Оплата', 'Не прошла', 'danger', 'failed'),
        receipt: badge('Чек', 'Не пробит', 'danger', 'failed'),
      }
    case 'unknown':
      return {
        money:
          verdict?.kind === 'unknown' && verdict.card?.outcome === 'approved'
            ? badge('Оплата', 'Деньги списаны', 'danger', 'unknown')
            : badge('Оплата', 'Исход неизвестен', 'warning', 'unknown'),
        receipt: badge('Чек', 'Исход неизвестен', 'warning', 'unknown'),
      }
  }
}

/** Неразобранная операция: из журнала агента (после перезагрузки страницы) или итог этой продажи. */
function operationOf(session: AgentSessionModel, state: OwnSaleState, verdict: SaleVerdict | null): SaleOperationVm | null {
  const entry = session.inDoubt.find((item) => !item.parked)
  const pendingId = session.pendingUnknown
  if (entry === undefined && pendingId === null && !(state.phase === 'unknown' && verdict?.kind === 'unknown')) return null
  const operationId = entry?.operationId ?? pendingId ?? ''
  const needsVoid = (verdict?.kind === 'unknown' && verdict.card?.outcome === 'approved') || entry?.cardNeedsManualVoid === true
  return {
    operationId,
    kindText: entry === undefined ? 'Чек' : KIND_TEXT[entry.kind],
    stateText: needsVoid ? 'Деньги списаны, исход чека неизвестен' : 'Исход неизвестен',
    tone: needsVoid ? 'danger' : 'warning',
    details: entry?.total == null ? [] : [`Сумма ${formatKopecks(Math.round(entry.total * 100))}`],
    version: null,
    canReconcile: operationId !== '',
    manualRequired: needsVoid,
    polling: false,
    accepted: false,
  }
}

function commandOf(verdict: SaleVerdict | null): SaleCommandVm | null {
  if (verdict === null || verdict.kind === 'sold') return null
  if (verdict.kind === 'unknown' && verdict.error?.code === 'agent-unreachable') return { kindText: 'Чек', phase: 'unknown', text: verdict.error.text }
  if (verdict.kind === 'not-sold' && verdict.error?.code === 'not-delivered') return { kindText: 'Чек', phase: 'not-arrived', text: verdict.error.text }
  return null
}

function feedbackOf(verdict: SaleVerdict | null): SaleFeedback | null {
  if (verdict === null || verdict.kind === 'sold' || verdict.error === null) return null
  return {
    command: 'sale.pay',
    target: '',
    phase: verdict.kind === 'unknown' ? 'unknown' : 'rejected',
    error: { kind: 'client', message: verdict.error.text, code: verdict.error.code, correlationId: null },
    note: verdict.error.action,
  }
}

function lockReason(state: OwnSaleState): string | null {
  switch (state.phase) {
    case 'composing':
      return null
    case 'paying':
      return 'Идёт оплата — корзину менять нельзя.'
    case 'finished':
      return 'Чек пробит. Начните новую продажу.'
    case 'failed':
      return 'Чек не пробит. Нажмите «Новая продажа», чтобы исправить корзину и повторить.'
    case 'unknown':
      return 'Исход оплаты неизвестен — корзину менять нельзя. Повторите проведение, закройте продажу или отложите её.'
  }
}

function quantityTotal(lines: readonly CartLine[]): string | null {
  const units = new Set(lines.map((line) => line.unit))
  const [unit] = [...units]
  if (units.size !== 1 || unit === undefined || unit === null) return null
  const milli = lines.reduce((sum, line) => sum + line.quantityMilli, 0)
  return `${quantityText(milli)} ${unit}`
}

/** Отложенные продажи кассы: деньги взяты, чек не пробит, касса свободна. Списка в окне пока нет — заметка без ссылки. */
function parkedNoticeOf(session: AgentSessionModel): SaleVm['parkedNotice'] {
  const parked = session.inDoubt.filter((entry) => entry.parked).length
  if (parked === 0) return null
  return { text: `Отложено продаж: ${parked}. Деньги взяты, чек не пробит — запись хранится в кассовом агенте.`, linkLabel: 'Список отложенных' }
}

const noticeVm = (busy: boolean) => (item: OwnNotice): SaleNoticeVm => ({
  key: item.key,
  text: item.text,
  tone: item.tone,
  actionLabel: item.action === 'print' ? 'Допечатать' : null,
  busy,
})

export function buildOwnSaleVm(input: OwnSaleVmInput): SaleVm {
  const { state, session, catalog } = input
  const words = input.words ?? initialOwnWords
  const verdict = state.verdict
  const cart = state.cart
  const problems = cart.lines.length === 0 ? [] : cartProblems(cart)
  const gate = session.gate
  const composing = state.phase === 'composing'
  const hasLines = cart.lines.length > 0
  const total = composing ? cartTotal(cart) : (state.paidTotalKopecks ?? cartTotal(cart))
  const blockedReason = !hasLines ? null : !gate.open ? gate.text : (problems[0]?.text ?? null)
  const canPay = composing && hasLines && gate.open && problems.length === 0 && session.busy === null
  const check: SaleCheckVm | null = !hasLines
    ? null
    : {
        canSell: gate.open && problems.length === 0,
        summary: problems.length > 0 ? 'В чеке есть товар, который нельзя продать' : gate.open ? '' : 'Касса сейчас не готова к продаже',
        cartReasons: gate.open
          ? []
          : [{ code: gate.reason ?? 'closed', text: gate.text, detail: gate.advice, fixBy: gate.action === null ? 'administrator' : 'cashier', removable: false }],
      }
  const payButtons: readonly PayButtonVm[] = (['Cash', 'Card'] as const).map((kind) => ({
    kind,
    label: PAYMENT_TEXT[kind],
    enabled: canPay,
    primary: state.paymentKind === kind,
    busy: state.phase === 'paying' && state.paidKind === kind,
  }))
  const selected = cart.lines.find((line) => line.id === state.selectedLineId)
  const { money, receipt } = badges(state, verdict)
  const finished: SaleVm['finished'] =
    state.phase === 'finished' && verdict?.kind === 'sold'
      ? {
          receiptId: verdict.fiscal?.fiscalDocumentNumber == null ? null : String(verdict.fiscal.fiscalDocumentNumber),
          totalText: formatKopecks(total),
          paymentText: PAYMENT_TEXT[state.paidKind ?? state.paymentKind],
          issuedOutside: false,
        }
      : null
  const cashierReady = input.cashierReady ?? true
  // «Повторить оплату» и «Наличными» в окне: та же проверка, что у кнопок оплаты, но и после отказа (корзина та же).
  const canPayNow = hasLines && gate.open && problems.length === 0 && session.busy === null && cashierReady && state.phase !== 'paying'
  const repeatBlocked = canPayNow
    ? null
    : !gate.open
      ? gate.text
      : (problems[0]?.text ?? (session.busy !== null ? 'Кассовый агент занят другой командой — подождите.' : cashierReady ? null : 'Кассир не подтверждён: подтвердите кассира PIN-ом.'))
  const payment = buildOwnPaymentFlow({ resourceId: input.resourceId, state, session, words, attempt: input.attempt ?? null, canPayNow, payBlockedReason: repeatBlocked })
  return {
    resourceId: input.resourceId,
    source: { name: 'Этот компьютер: касса и терминал', how: null, alternatives: 1 },
    // Корзину можно собирать, пока касса не готова: готовность проверяется при оплате, а причина видна в проверке корзины.
    placeReady: true,
    saleQuery: READY_QUERY,
    saleId: hasLines || !composing ? 'local' : null,
    paymentKind: state.paymentKind,
    canChoosePaymentKind: composing,
    lines: cart.lines.map((line) => lineVm(line, state, problems)),
    totalText: formatKopecks(total),
    totalPending: false,
    cartState: hasLines ? 'synced' : 'empty',
    cartLocked: !composing,
    cartLockedReason: lockReason(state),
    money,
    receipt,
    lastMessage: null,
    canPay,
    payText: canPay ? `Оплатить ${formatKopecks(total)}` : (blockedReason ?? 'Добавьте товар в чек'),
    payBlockedReason: canPay ? null : blockedReason,
    // Оплата и чек у агента — одна команда: отдельного «пробить чек» нет.
    canFiscalize: false,
    canCancel: composing && hasLines,
    operation: operationOf(session, state, verdict),
    command: commandOf(verdict),
    finished,
    canMarkIssuedOutside: false,
    outside: null,
    feedbackHint: verdict !== null && verdict.kind !== 'sold' && verdict.error?.action != null ? { text: verdict.error.action } : null,
    activeRefund: null,
    picker: catalog.picker,
    feedback: feedbackOf(verdict),
    customerContact: state.contact,
    canEditContact: composing,
    canEditPrice: false,
    check,
    selectedLine: selected === undefined ? null : lineVm(selected, state, problems),
    linesText: `Позиций: ${cart.lines.length}`,
    quantityTotalText: quantityTotal(cart.lines),
    search: catalog.search,
    held: { query: READY_QUERY, carts: [] },
    canHold: false,
    canClear: composing && hasLines,
    payButtons,
    payment,
    pricesAtText: catalog.pricesAtText,
    // Отказ кассы без окна (смена закрыта, агент не ответил) — строкой блока состояния, не уведомлением внизу; при окне — в нём.
    paymentStatus: composing && payment === null && session.notice?.tone === 'error' ? session.notice.text : null,
    parkedNotice: parkedNoticeOf(session),
    notices: words.notices.map(noticeVm(session.busy !== null)),
  }
}
